import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarCheck, Download, FileText, Home, LayoutDashboard, Plus } from "lucide-react";
import { api, errMsg, fileUrl } from "../lib/api";
import { PublicShell } from "../components/PublicShell";
import { CodeGate } from "../components/CodeGate";
import { Card, ErrorBox, Spinner } from "../components/kd";
import { StatusBadge } from "../components/Badges";
import { Chat, Timeline } from "../components/CaseParts";
import { FileUploader } from "../components/FileUploader";
import { DATEITYP, fmtDate, fmtDateTime, fmtSize } from "../lib/format";

function Details({ token, d, reload, readOnly }: { token: string; d: any; reload: () => void; readOnly?: boolean }) {
  return (
    <div className="space-y-5">
      <Card>
        <p className="text-sm text-text-muted">Fallnummer</p>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 data-testid="status-fallnummer">{d.fallnummer}</h1>
          <StatusBadge status={d.status} testid="status-current-badge" />
        </div>
        <p className="mt-2" data-testid="status-patient">Patient: {d.patient} · {d.grund}</p>
        <p className="text-sm text-text-muted">Eingegangen am {fmtDate(d.erstellt_am)}</p>
        {d.termin_datum && <p className="mt-3 flex items-center gap-2 rounded-lg bg-primary-light p-3 text-primary" data-testid="status-termin"><CalendarCheck className="h-5 w-5" aria-hidden />Termin: {fmtDateTime(d.termin_datum)}</p>}
        {d.bericht && (
          <a href={fileUrl(d.bericht.url)} className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-4 font-medium text-white no-underline hover:bg-primary-dark" data-testid="status-bericht-download">
            <FileText className="h-5 w-5" aria-hidden />Bericht herunterladen (PDF)
          </a>
        )}
      </Card>
      <Card><h2 className="mb-4">Verlauf</h2><Timeline items={d.verlauf} /></Card>
      <Card><h2 className="mb-4">Nachrichten</h2><fieldset disabled={readOnly}><Chat messages={d.nachrichten} me="UEBERWEISER" ki={readOnly ? undefined : { token }} onSend={async (text) => { await api.post(`/public/fall/${token}/nachrichten`, { text }); reload(); }} /></fieldset></Card>
      <Card>
        <h2 className="mb-4">Ihre Dateien</h2>
        {d.dateien.length === 0 && <p className="mb-4 text-text-muted">Noch keine Dateien hochgeladen.</p>}
        <ul className="mb-4 divide-y divide-border" data-testid="status-files">
          {d.dateien.map((f: any) => (
            <li key={f.id} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 truncate">{f.original_name} <span className="text-sm text-text-muted">({DATEITYP[f.typ]}, {fmtSize(f.groesse_bytes)})</span></span>
              <a href={fileUrl(f.url)} className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-bg" aria-label="Herunterladen"><Download className="h-5 w-5" aria-hidden /></a>
            </li>
          ))}
        </ul>
        {d.upload_erlaubt && !readOnly && <FileUploader uploadUrl={`/public/fall/${token}/dateien`} onUploaded={reload} />}
      </Card>
    </div>
  );
}

export function PreviewCase() {
  const { upId, token } = useParams();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/admin/vorschau/${upId}/fall/${token}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [upId, token]);
  if (!d) return err ? <ErrorBox>{err}</ErrorBox> : <Spinner />;
  return <div className="mx-auto max-w-3xl" data-testid="preview-case"><Details token={token as string} d={d} reload={() => undefined} readOnly /></div>;
}

function StatusNav({ slug }: { slug?: string }) {
  const [konto, setKonto] = useState(false);
  useEffect(() => { api.get("/konto/me").then(() => setKonto(true)).catch(() => undefined); }, []);
  const cls = "inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-surface px-3 font-medium text-text no-underline transition-colors hover:bg-bg";
  return (
    <nav className="mb-5 flex flex-wrap gap-2" aria-label="Weiter" data-testid="status-nav">
      {konto && <Link to="/ueberweiser" className={cls} data-testid="status-nav-overview"><LayoutDashboard className="h-4 w-4 text-primary" aria-hidden />Zur Übersicht</Link>}
      {slug && <Link to={`/u/${slug}`} className={cls} data-testid="status-nav-new"><Plus className="h-4 w-4 text-primary" aria-hidden />Neue Überweisung</Link>}
      {!konto && <Link to="/" className={cls} data-testid="status-nav-home"><Home className="h-4 w-4 text-primary" aria-hidden />Startseite</Link>}
    </nav>
  );
}

export default function StatusPage() {
  const { token } = useParams();
  const [info, setInfo] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get(`/public/fall/${token}`).then((r) => setDetail(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);

  useEffect(() => {
    api.get(`/public/fall/${token}/info`).then((r) => { setInfo(r.data); if (r.data.angemeldet) load(); }).catch((e) => setErr(errMsg(e)));
  }, [token, load]);

  if (!info) return <PublicShell>{err ? <Card><ErrorBox testid="status-error">{err}</ErrorBox></Card> : <Spinner />}</PublicShell>;
  return (
    <PublicShell praxis={info.praxis}>
      <StatusNav slug={info.praxis?.slug} />
      <ErrorBox testid="status-error">{err}</ErrorBox>
      {!detail ? (
        <Card>
          <CodeGate
            title="Status Ihrer Überweisung"
            fixedEmailHint={info.email_hinweis}
            onRequest={() => api.post(`/public/fall/${token}/code`)}
            onVerify={async (_e, code) => { await api.post(`/public/fall/${token}/code/pruefen`, { code }); setErr(""); load(); }}
          />
        </Card>
      ) : (
        <Details token={token as string} d={detail} reload={load} />
      )}
    </PublicShell>
  );
}
